/**
 * Sprint 24 — EquippedWithChip + EquippedWithLegend.
 *
 * The agent-profile sidebar chip (Q13): a connection name preceded by a 6px
 * type-coloured dot. Three colours — purple (first-party), amber (MCP), teal
 * (Wrapped). The legend strip below the chip wrap explains the dots.
 */

import type { CSSProperties } from "react";

export type EquippedWithKind = "first-party" | "mcp" | "wrapped";

export const DOT_COLOR: Record<EquippedWithKind, string> = {
  "first-party": "#534AB7",
  mcp: "#BA7517",
  wrapped: "#1D9E75",
};

export interface EquippedWithChipProps {
  kind: EquippedWithKind;
  name: string;
  title?: string;
  onClick?: () => void;
}

export function EquippedWithChip({ kind, name, title, onClick }: EquippedWithChipProps) {
  const pill: CSSProperties = {
    fontSize: 11,
    padding: "3px 8px 3px 6px",
    borderRadius: 999,
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: "var(--color-text-secondary)",
    fontFamily: "var(--font-sans)",
    maxWidth: 180,
    cursor: onClick ? "pointer" : "default",
  };
  const dot: CSSProperties = {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: DOT_COLOR[kind],
    flexShrink: 0,
  };
  const label: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  return (
    <button type="button" style={pill} title={title ?? name} onClick={onClick} disabled={!onClick}>
      <span style={dot} />
      <span style={label}>{name}</span>
    </button>
  );
}

/** The three-item legend strip rendered below the chip wrap. */
export function EquippedWithLegend() {
  const strip: CSSProperties = {
    marginTop: 10,
    paddingTop: 8,
    borderTop: "0.5px solid var(--color-border-tertiary)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 10,
    color: "var(--color-text-tertiary)",
    flexWrap: "wrap",
  };
  const item: CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4 };
  const dot = (kind: EquippedWithKind): CSSProperties => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: DOT_COLOR[kind],
  });
  return (
    <div style={strip}>
      <span style={item}>
        <span style={dot("first-party")} /> 1st-party
      </span>
      <span style={item}>
        <span style={dot("mcp")} /> MCP
      </span>
      <span style={item}>
        <span style={dot("wrapped")} /> Wrapped
      </span>
    </div>
  );
}
